"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.UserAccount = exports.OpenPosition = exports.MarketState = exports.Instance = exports.StateTag = void 0;
const web3_js_1 = require("@solana/web3.js");
const bn_js_1 = __importDefault(require("bn.js"));
const borsh_1 = require("borsh");
const spl_token_1 = require("@solana/spl-token");
const instructions_1 = require("./instructions");
var StateTag;
(function (StateTag) {
    StateTag[StateTag["Uninitialized"] = 0] = "Uninitialized";
    StateTag[StateTag["MarketState"] = 1] = "MarketState";
    StateTag[StateTag["UserAccount"] = 2] = "UserAccount";
    StateTag[StateTag["MemoryPage"] = 3] = "MemoryPage";
    StateTag[StateTag["Instance"] = 4] = "Instance";
})(StateTag = exports.StateTag || (exports.StateTag = {}));
class PointerOption {
    constructor(obj) {
        this.pointer = obj.pointer;
    }
}
class PageInfo {
    constructor(obj) {
        this.uninitializedMemoryIndex = obj.uninitializedMemoryIndex;
        this.address = new web3_js_1.PublicKey(obj.address);
        this.freeSlotListHd = obj.freeSlotListHd.pointer;
    }
}
PageInfo.size = 41;
class Pages {
    constructor(obj) {
        this.pages = obj.pages;
    }
}
class Instance {
    constructor(obj) {
        this.numberOfPages = obj.numberOfPages;
        this.pages = [];
        this.shortsPointer = obj.shortsPointer.pointer;
        this.longsPointer = obj.longsPointer.pointer;
        this.garbagePointer = obj.garbagePointer.pointer;
    }
    static async retrieve(connection, instanceAccount) {
        let instanceData = await connection.getAccountInfo(instanceAccount, "processed");
        if (instanceData === null) {
            throw new Error("Invalid instance account provided");
        }
        if (instanceData.data[0] !== StateTag.Instance) {
            throw new Error("The provided account isn't an instance account");
        }
        let res = borsh_1.deserializeUnchecked(this.schema, Instance, instanceData.data.slice(1, this.headerSize));
        for (let i = 0; i < res.numberOfPages; i++) {
            let slice = instanceData.data.slice(this.headerSize + i * PageInfo.size);
            let page = borsh_1.deserializeUnchecked(this.schema, PageInfo, slice);
            res.pages.push(page);
        }
        return res;
    }
}
exports.Instance = Instance;
Instance.headerSize = 21;
//@ts-ignore
Instance.schema = new Map([
    [
        Instance,
        {
            kind: "struct",
            fields: [
                ["version", "u8"],
                ["shortsPointer", PointerOption],
                ["longsPointer", PointerOption],
                ["garbagePointer", PointerOption],
                ["numberOfPages", "u32"],
            ],
        },
    ],
    [
        PointerOption,
        {
            kind: "enum",
            values: [
                ["None", [0]],
                ["pointer", "u32"],
            ],
        },
    ],
    [
        PageInfo,
        {
            kind: "struct",
            fields: [
                ["address", [32]],
                ["uninitializedMemoryIndex", "u32"],
                ["freeSlotListHd", PointerOption],
            ],
        },
    ],
    [
        Pages,
        {
            kind: "struct",
            fields: [["pages", [PageInfo]]],
        },
    ],
]);
class MarketState {
    constructor(obj) {
        this.signerNonce = obj.signerNonce;
        this.marketSymbol = obj.marketSymbol.toString();
        this.oracleAddress = new web3_js_1.PublicKey(obj.oracleAddress);
        this.adminAddress = new web3_js_1.PublicKey(obj.adminAddress);
        this.vaultAddress = new web3_js_1.PublicKey(obj.vaultAddress);
        this.quoteDecimals = obj.quoteDecimals;
        this.coinDecimals = obj.coinDecimals;
        this.totalCollateral = obj.totalCollateral.toNumber();
        this.vCoinAmount = obj.vCoinAmount.toNumber();
        this.vQuoteAmount = obj.vQuoteAmount.toNumber();
        this.openShortsVCoin = obj.openShortsVCoin.toNumber();
        this.openLongsVCoin = obj.openLongsVCoin.toNumber();
        this.openShortsVPc = obj.openShortsVPc.toNumber();
        this.openLongsVPc = obj.openShortsVCoin.toNumber();
        this.totalUserBudgets = obj.totalUserBudgets.fromTwos(64).toNumber();
        this.totalFeeBudget = obj.totalFeeBudget.toNumber();
        this.rebalancingFunds = obj.rebalancingFunds.toNumber();
        this.rebalancedVCoin = obj.rebalancedVCoin.fromTwos(64).toNumber();
        this.lastFundingTimestamp = obj.lastFundingTimestamp.toNumber();
        this.lastRecordingTimestamp = obj.lastRecordingTimestamp.toNumber();
        this.fundingSamplesCount = obj.fundingSamplesCount;
        this.fundingSamplesSum = obj.fundingSamplesSum.fromTwos(64).toNumber();
        this.fundingHistoryOffset = obj.fundingHistoryOffset;
        this.fundingHistory = [];
        this.fundingBalancingFactors = [];
        for (let i = 0; i < 16; i++) {
            let offset = 8 * i;
            this.fundingHistory.push(new bn_js_1.default(obj.fundingHistory.slice(offset, offset + 8), "le")
                .fromTwos(64)
                .toNumber());
            this.fundingBalancingFactors.push(new bn_js_1.default(obj.fundingBalancingFactors.slice(offset, offset + 8), "le").toNumber());
        }
        this.instanceAddresses = obj.instanceAddresses.map((s) => new web3_js_1.PublicKey(s));
    }
    static async retrieve(connection, marketAccount) {
        let marketStateData = await connection.getAccountInfo(marketAccount, "processed");
        if (!marketStateData) {
            throw new Error("Invalid market account provided");
        }
        if (marketStateData.data[0] !== StateTag.MarketState) {
            throw new Error("The provided account isn't a market account");
        }
        let res = borsh_1.deserializeUnchecked(this.schema, MarketState, marketStateData.data.slice(1));
        res.marketAccount = marketAccount;
        res.instances = await Promise.all(res.instanceAddresses.map((s) => Instance.retrieve(connection, s)));
        return res;
    }
    async getQuoteMint(connection) {
        let vaultInfo = await connection.getAccountInfo(this.vaultAddress, "processed");
        if (vaultInfo === null) {
            throw new Error("Couldn't fetch the market vault's data");
        }
        return new web3_js_1.PublicKey(spl_token_1.AccountLayout.decode(vaultInfo.data).mint);
    }
    async getMarketSigner(programId) {
        return web3_js_1.PublicKey.createProgramAddress([this.marketAccount.toBuffer(), Buffer.from([this.signerNonce])], programId);
    }
    getMarkPrice() {
        return this.vQuoteAmount / this.vCoinAmount;
    }
    getFundingRatioLongShort() {
        let ratio = this.fundingSamplesSum / (this.fundingSamplesCount * 2 ** 32);
        if (ratio > 0) {
            let fundingRatioLongs = ratio;
            let balancing_factor = Math.max(this.openLongsVCoin / this.openShortsVCoin, 24 / 100);
            let fundingRatioShorts = balancing_factor * ratio;
            return { fundingRatioLongs, fundingRatioShorts };
        }
        else {
            let fundingRatioShorts = ratio;
            let balancing_factor = Math.max(this.openShortsVCoin / this.openLongsVCoin, 24 / 100);
            let fundingRatioLongs = balancing_factor * ratio;
            return { fundingRatioLongs, fundingRatioShorts };
        }
    }
    getOpenInterest() {
        return { shorts: this.openShortsVCoin, longs: this.openLongsVCoin };
    }
    getSlippageEstimation(side, vPcAmount) {
        let markPrice = this.vQuoteAmount / this.vCoinAmount;
        let actualPrice;
        if (side == instructions_1.PositionType.Long) {
            actualPrice = (this.vQuoteAmount + vPcAmount) / this.vCoinAmount;
        }
        else {
            actualPrice = (this.vQuoteAmount - vPcAmount) / this.vCoinAmount;
        }
        return Math.abs(markPrice - actualPrice) / markPrice;
    }
    getLiquidationIndex(side, vCoinAmount, collateral) {
        let m = 1 / 20;
        let k = this.vCoinAmount * this.vQuoteAmount;
        let sideSign = side * 2 - 1;
        let py = (vCoinAmount / (this.vCoinAmount - vCoinAmount * sideSign)) *
            this.vQuoteAmount;
        let f = (py - sideSign * collateral) / (1 - sideSign * m);
        if (f <= 0) {
            return 0;
        }
        let liqIndex = (f ** 2 / (4 * k)) *
            (Math.sqrt(1 + (4 * k) / (f * vCoinAmount)) + sideSign) ** 2;
        return liqIndex;
    }
}
exports.MarketState = MarketState;
MarketState.schema = new Map([
    [
        MarketState,
        {
            kind: "struct",
            fields: [
                ["version", "u8"],
                ["signerNonce", "u8"],
                ["marketSymbol", [32]],
                ["oracleAddress", [32]],
                ["adminAddress", [32]],
                ["vaultAddress", [32]],
                ["quoteDecimals", "u8"],
                ["coinDecimals", "u8"],
                ["totalCollateral", "u64"],
                ["totalUserBudgets", "u64"],
                ["totalFeeBudget", "u64"],
                ["rebalancingFunds", "u64"],
                ["rebalancedVCoin", "u64"],
                ["vCoinAmount", "u64"],
                ["vQuoteAmount", "u64"],
                ["openShortsVCoin", "u64"],
                ["openLongsVCoin", "u64"],
                ["openShortsVPc", "u64"],
                ["openLongsVPc", "u64"],
                ["lastFundingTimestamp", "u64"],
                ["lastRecordingTimestamp", "u64"],
                ["fundingSamplesCount", "u8"],
                ["fundingSamplesSum", "u64"],
                ["fundingHistoryOffset", "u8"],
                ["fundingHistory", [128]],
                ["fundingBalancingFactors", [128]],
                ["instanceAddresses", [[32]]],
            ],
        },
    ],
]);
class OpenPosition {
    constructor(obj) {
        this.lastFundingOffset = obj.lastFundingOffset;
        this.instanceIndex = obj.instanceIndex;
        this.side = obj.side;
        this.liquidationIndex =
            obj.liquidationIndex.ushrn(32).toNumber() +
                obj.liquidationIndex.maskn(32).toNumber() / 2 ** 32;
        this.collateral = obj.collateral.toNumber();
        this.slotNumber = obj.slotNumber.toNumber();
        this.vCoinAmount = obj.vCoinAmount.toNumber();
        this.vPcAmount = obj.vPcAmount.toNumber();
    }
}
exports.OpenPosition = OpenPosition;
OpenPosition.LEN = 43;
class UserAccount {
    constructor(obj) {
        this.owner = new web3_js_1.PublicKey(obj.owner);
        this.market = new web3_js_1.PublicKey(obj.market);
        this.active = obj.active == 1;
        this.balance = obj.balance.toNumber();
        this.lastFundingOffset = obj.lastFundingOffset;
        this.openPositions = obj.openPositions;
    }
    static async retrieve(connection, account) {
        let accountData = await connection.getAccountInfo(account, "processed");
        if (accountData === null) {
            throw new Error("Invalid market account provided");
        }
        return UserAccount.parse(account, accountData.data);
    }
    static parse(address, data) {
        if (data[0] !== StateTag.UserAccount) {
            throw new Error("The provided account isn't a user account");
        }
        let res = borsh_1.deserializeUnchecked(this.schema, UserAccount, data.slice(1));
        res.address = address;
        return res;
    }
}
exports.UserAccount = UserAccount;
UserAccount.LEN = 80;
//@ts-ignore
UserAccount.schema = new Map([
    [
        UserAccount,
        {
            kind: "struct",
            fields: [
                ["version", "u8"],
                ["owner", [32]],
                ["active", "u8"],
                ["market", [32]],
                ["balance", "u64"],
                ["lastFundingOffset", "u8"],
                ["openPositions", [OpenPosition]],
            ],
        },
    ],
    [
        OpenPosition,
        {
            kind: "struct",
            fields: [
                ["lastFundingOffset", "u8"],
                ["instanceIndex", "u8"],
                ["side", "u8"],
                ["liquidationIndex", "u64"],
                ["collateral", "u64"],
                ["slotNumber", "u64"],
                ["vCoinAmount", "u64"],
                ["vPcAmount", "u64"],
            ],
        },
    ],
]);
//# sourceMappingURL=state.js.map